import { Aggregation } from '@magentoTypes'
import { Image } from '~/elements/Image/Image'
import { useTransition, animated } from '@react-spring/web'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { MouseEventHandler, ReactNode } from 'react'
import { parseFilterString } from '../lib/parseFilterString'
import { Media } from '~/lib/Media'

type Props = {
  className?: string
  setShowFilters: (initialOpen?: number) => void
  aggregations: Aggregation[]
}
type FilterBubbleProps = {
  children: ReactNode
  onClick: MouseEventHandler<HTMLButtonElement>
  title: string
}

export const FilterBubble = ({ children, onClick, title }: FilterBubbleProps) => {
  return (
    <button
      onClick={onClick}
      title={title}
      className="flex flex-row items-center whitespace-nowrap rounded-[99px] bg-grey-light py-[6px] px-[16px] xl:px-5 xl:py-[10px]"
    >
      {children}
    </button>
  )
}

export const FilterTrigger = ({ className, setShowFilters, aggregations }: Props) => {
  const { t } = useTranslation()
  const router = useRouter()

  const calculateItemsToShow = (): (Aggregation & { amount?: number })[] => {
    const filters = parseFilterString(router.query.filter as string)

    const usedFilters = Object.keys(filters).map((filter) => ({
      ...aggregations.find((aggregation) => aggregation.attribute_code === filter),
      amount: filters[filter]?.length,
    })) as Aggregation[]

    // if filters were selected, only show the selected bubbles
    // otherwise show the first 4
    return usedFilters.length > 0 ? usedFilters.slice(0, 3) : aggregations.slice(0, 4)
  }

  const transitions = useTransition(calculateItemsToShow(), {
    keys: (item) => item.attribute_code,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0, position: 'absolute' },
  })

  const handleClick = (index?: number) => {
    setShowFilters(index !== -1 ? index : undefined)
  }

  return (
    <>
      <Media greaterThanOrEqual="lg">
        <ul className={clsx('grid grid-flow-col justify-start gap-2', className)}>
          {transitions((style, item, _state, i) =>
            item ? (
              <animated.li style={style}>
                <FilterBubble
                  title={`${item.label}`}
                  onClick={() =>
                    handleClick(
                      aggregations.findIndex(
                        (aggregation) => aggregation.attribute_code === item.attribute_code,
                      ),
                    )
                  }
                >
                  {item.label}{' '}
                  {!!item.amount && (
                    <span className="ml-2 flex rounded-full bg-primary-white px-3">
                      {item.amount}
                    </span>
                  )}
                </FilterBubble>
              </animated.li>
            ) : null,
          )}
          <li>
            <FilterBubble title={t('Filter All')} onClick={() => handleClick()}>
              {t('Filter All')}
            </FilterBubble>
          </li>
        </ul>
      </Media>
      <Media lessThan="lg">
        <button onClick={() => handleClick()} className={clsx('flex items-center', className)}>
          <Image
            src={'/images/Icons/filter.svg'}
            width={16}
            height={24}
            placeholder="empty"
            alt="filter-icon"
            className="mr-1"
          />
          {t('Filter')}
        </button>
      </Media>
    </>
  )
}
